import React from 'react'
import './CalibrationWizard.css'

interface CalibrationWizardProps {
  step: number // 0=heel, 1=MT1, 2=MT5, 3=complete
  calibrationPoints: {
    heel?: { x: number; y: number; z: number }
    mt1?: { x: number; y: number; z: number }
    mt5?: { x: number; y: number; z: number }
  }
  onCancel: () => void
  onConfirm?: () => void
  onReset?: () => void
}

export const CalibrationWizard: React.FC<CalibrationWizardProps> = ({
  step,
  calibrationPoints,
  onCancel,
  onConfirm,
  onReset
}) => {
  const getStepInstruction = () => {
    switch (step) {
      case 0:
        return {
          title: 'Step 1 of 3: Heel Center',
          instruction: 'Click on the CENTER of the HEEL (back of the foot)',
          icon: '👠'
        }
      case 1:
        return {
          title: 'Step 2 of 3: 1st Metatarsal Head',
          instruction: 'Click on the 1st METATARSAL HEAD (big toe side, ball of foot)',
          icon: '👣'
        }
      case 2:
        return {
          title: 'Step 3 of 3: 5th Metatarsal Head',
          instruction: 'Click on the 5th METATARSAL HEAD (pinky toe side, ball of foot)',
          icon: '🦶'
        }
      case 3:
        return {
          title: 'Calibration Complete!',
          instruction: 'All 3 calibration points have been captured',
          icon: '✅'
        }
      default:
        return {
          title: 'Calibration',
          instruction: 'Click to select calibration points',
          icon: '📍'
        }
    }
  }

  const stepInfo = getStepInstruction()
  const progress = step >= 3 ? 100 : ((step / 3) * 100)

  return (
    <div className="calibration-wizard-overlay">
      <div className="calibration-wizard">
        <div className="wizard-header">
          <h3>{stepInfo.icon} {stepInfo.title}</h3>
          <button className="close-btn" onClick={onCancel} title="Cancel Calibration">
            ✕
          </button>
        </div>

        <div className="wizard-body">
          <p className="instruction">{stepInfo.instruction}</p>

          {/* Progress bar */}
          <div className="progress-container">
            <div className="progress-bar" style={{ width: `${progress}%` }}></div>
          </div>
          <p className="progress-text">Progress: {step}/3 points captured</p>

          {/* Captured points summary */}
          <div className="points-summary">
            <div className={`point-item ${calibrationPoints.heel ? 'captured' : 'pending'}`}>
              <span className="point-label">🔴 Heel:</span>
              <span className="point-coords">
                {calibrationPoints.heel 
                  ? `(${calibrationPoints.heel.x.toFixed(2)}, ${calibrationPoints.heel.y.toFixed(2)}, ${calibrationPoints.heel.z.toFixed(2)})`
                  : 'Not captured'}
              </span>
            </div>
            <div className={`point-item ${calibrationPoints.mt1 ? 'captured' : 'pending'}`}>
              <span className="point-label">🟢 MT1 (Big Toe):</span>
              <span className="point-coords">
                {calibrationPoints.mt1 
                  ? `(${calibrationPoints.mt1.x.toFixed(2)}, ${calibrationPoints.mt1.y.toFixed(2)}, ${calibrationPoints.mt1.z.toFixed(2)})`
                  : 'Not captured'}
              </span>
            </div>
            <div className={`point-item ${calibrationPoints.mt5 ? 'captured' : 'pending'}`}>
              <span className="point-label">🔵 MT5 (Pinky):</span>
              <span className="point-coords">
                {calibrationPoints.mt5 
                  ? `(${calibrationPoints.mt5.x.toFixed(2)}, ${calibrationPoints.mt5.y.toFixed(2)}, ${calibrationPoints.mt5.z.toFixed(2)})`
                  : 'Not captured'}
              </span>
            </div>
          </div>

          {/* Help text */}
          <div className="help-text">
            <p>💡 <strong>Tip:</strong> Rotate the 3D model to get a clear view of each landmark before clicking.</p>
          </div>

          {/* Action buttons when all points captured */}
          {step >= 3 && (
            <div className="action-buttons">
              <button className="btn-reset" onClick={onReset}>
                🔄 Reset Points
              </button>
              <button className="btn-confirm" onClick={onConfirm}>
                ✅ Confirm & Save
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

